/**
 * SSE client for panel-api's Support agent stream (`GET /api/v1/support/stream`).
 *
 * Auth mirrors `email-status-api.ts`: Bearer token plus `X-Tenant-ID`, read off
 * a `fetch` body because native `EventSource` can send neither. Frame shapes
 * mirror `apps/panel-api/src/support/utils/agent-stream-frame.ts` — kept in
 * lockstep by hand; they are not in the GraphQL schema.
 */

import { parseSseEvents } from '../../../utils/sse';

const API_BASE = import.meta.env.VITE_API_ENDPOINT ?? 'http://localhost:4200';

export interface TicketEventFrame {
  type: 'ticket_event';
  streamId: string;
  /** The outbox id. Delivery is at-least-once: dedupe on this. */
  eventId: string;
  ticketId: string;
  eventType: string;
  seq: number | null;
  visibility: string | null;
  actor: { type: string; id: string | null };
  field: string | null;
  from: string | null;
  to: string | null;
  messageId: string | null;
  createdAt: string | null;
}

export interface NotificationFrame {
  type: 'notification';
  streamId: string;
  notificationId: string;
  kind: string;
  ticketId: string;
  ticketNumber: string;
  title: string | null;
  createdAt: string | null;
}

export interface TypingFrame {
  type: 'typing';
  ticketId: string;
  actor: { type: string; id: string };
  /** An agent writing an internal note. */
  internal: boolean;
  expiresInMs: number;
}

export interface ReadyFrame {
  type: 'ready';
  /** false: refetch what is on screen (first connect, or a gap the server cannot replay). */
  resumed: boolean;
}

export type SupportStreamFrame = TicketEventFrame | NotificationFrame | TypingFrame | ReadyFrame;

/** Thrown when the server answers 503: live updates are off, not a drop worth retrying fast. */
export class SupportStreamUnavailableError extends Error {}

export interface SupportStreamOptions {
  getToken: () => Promise<string>;
  /** Stream another tenant's frames (the sandbox's) instead of the selected tenant's. */
  tenantId?: string;
  /** The last durable frame id seen; the server replays everything after it. */
  lastEventId: string | null;
  onFrame: (frame: SupportStreamFrame, id: string | null) => void;
  signal?: AbortSignal;
}

/**
 * Consume the stream until the server closes it or `signal` aborts. Resolves
 * on a clean end-of-stream, rejects when the connection cannot be opened; the
 * caller owns reconnects.
 */
export async function streamSupportEvents(options: SupportStreamOptions): Promise<void> {
  const { getToken, lastEventId, onFrame, signal } = options;
  const token = await getToken();
  const tenantId =
    options.tenantId ||
    localStorage.getItem('selectedTenantId') ||
    sessionStorage.getItem('selectedTenantId');

  const response = await fetch(`${API_BASE}/api/v1/support/stream`, {
    credentials: 'include',
    headers: {
      Accept: 'text/event-stream',
      Authorization: token ? `Bearer ${token}` : '',
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
      ...(lastEventId && { 'Last-Event-ID': lastEventId }),
    },
    signal,
  });

  if (response.status === 503) {
    throw new SupportStreamUnavailableError('Live updates are not available');
  }
  if (!response.ok || !response.body) {
    throw new Error(`Support stream unavailable (${response.status})`);
  }

  for await (const event of parseSseEvents<SupportStreamFrame>(response.body)) {
    onFrame(event.data, event.id);
  }
}
